import { useState } from 'react';
import type { ReactElement } from 'react';
import { assetToast } from '@xdocs/i18n';
import type { Locale as XdocsLocale } from '@xdocs/i18n';
import type { LandingContent } from '@xdocs/schemas';
import { Button, ConfirmDialog, Modal, Tabs, Textarea, cn, useI18n, useToast } from '@xdocs/ui';
import { requestTyped } from '@xdocs/ws-protocol';
import { sanitizeManuscriptHtml } from '../../../features/reader/sanitizeManuscriptHtml.js';
import { safeT } from '../../../i18n/safeT.js';
import { useWs } from '../../../ws/WsProvider.js';
import type { CmsBlockGroup } from './useCmsBlocks.js';
import { useCmsT } from './useCmsT.js';
import styles from './CmsBlockEditor.module.css';

export interface CmsBlockEditorProps {
  blockKey: string;
  group: CmsBlockGroup;
  onClose: () => void;
  onChanged: () => void;
}

function extractHtml(row: LandingContent | undefined): string {
  const value = row?.contentJsonb['html'];
  return typeof value === 'string' ? value : '';
}

/**
 * F61 block editor — per-locale (EN/RO) Draft → Publish workflow.
 * `landing_content.contentJsonb` is a free-form `jsonObject`
 * (`packages/schemas/src/content.ts`); this editor's working shape is
 * `{html: string}` (documented assumption — see returned open-issues list).
 * The preview only ever renders a SAVED row (`admin.cms.blocksSave`'s
 * response), never the live draft textarea, AND that value is still run
 * through {@link sanitizeManuscriptHtml} client-side before
 * `dangerouslySetInnerHTML` — the server side of `admin.cms.blocksSave`
 * doesn't exist yet as of this task (only the frozen schema/WS-op
 * contracts do), and this repo has already hit one case
 * (`features/reader/sanitizeManuscriptHtml.ts`) where a field documented as
 * "server-sanitized" wasn't actually sanitized on the write path — so this
 * doesn't trust that guarantee blindly for a handler that isn't written
 * yet, same allowlist sanitizer, no new dependency.
 */
export function CmsBlockEditor({ blockKey, group, onClose, onChanged }: CmsBlockEditorProps): ReactElement {
  const { t } = useCmsT();
  const [activeLocale, setActiveLocale] = useState<XdocsLocale>('en');
  const [rows, setRows] = useState<Record<XdocsLocale, LandingContent | undefined>>({ en: group.en, ro: group.ro });

  return (
    <Modal open onClose={onClose} title={t('adminCms:editor.title', { blockKey })} size="lg" scrollableBody>
      <div className={cn(styles['editor'])}>
        <Tabs<XdocsLocale>
          label={blockKey}
          value={activeLocale}
          onChange={setActiveLocale}
          items={[
            { value: 'en', label: 'EN' },
            { value: 'ro', label: 'RO' },
          ]}
        />

        {/* Keyed by locale so switching tabs mounts a FRESH pane whose local
            draft state initializes straight from `rows[activeLocale]` via
            `useState`'s lazy initializer — no effect-driven state reset
            (react-hooks/set-state-in-effect: "calling setState synchronously
            within an effect can trigger cascading renders"). */}
        <LocaleEditorPane
          key={activeLocale}
          blockKey={blockKey}
          locale={activeLocale}
          row={rows[activeLocale]}
          onSaved={(saved) => {
            setRows((prev) => ({ ...prev, [activeLocale]: saved }));
            onChanged();
          }}
        />
      </div>
    </Modal>
  );
}

interface LocaleEditorPaneProps {
  blockKey: string;
  locale: XdocsLocale;
  row: LandingContent | undefined;
  onSaved: (saved: LandingContent) => void;
}

function LocaleEditorPane({ blockKey, locale, row, onSaved }: LocaleEditorPaneProps): ReactElement {
  const { locale: uiLocale } = useI18n();
  const { t } = useCmsT();
  const { wsClient } = useWs();
  const toast = useToast();

  const [currentRow, setCurrentRow] = useState(row);
  const [draftHtml, setDraftHtml] = useState(extractHtml(row));
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isDirty = draftHtml !== extractHtml(currentRow);

  async function handleSaveDraft(): Promise<void> {
    setIsSaving(true);
    setError(null);
    try {
      const saved = await requestTyped(wsClient, 'admin.cms.blocksSave', {
        blockKey,
        locale,
        contentJsonb: { html: draftHtml },
      });
      setCurrentRow(saved);
      setDraftHtml(extractHtml(saved));
      toast.push(assetToast(uiLocale, 'success', t('adminCms:toast.draftSaved.action'), blockKey));
      onSaved(saved);
    } catch (err) {
      setError(safeT(uiLocale, (err as { message?: string }).message ?? 'errors:code.INTERNAL'));
    } finally {
      setIsSaving(false);
    }
  }

  async function handlePublish(): Promise<void> {
    if (!currentRow) return;
    setIsPublishing(true);
    setError(null);
    try {
      const published = await requestTyped(wsClient, 'admin.cms.blocksPublish', {
        blockKey,
        locale,
        version: currentRow.version,
      });
      setCurrentRow(published);
      toast.push(assetToast(uiLocale, 'success', t('adminCms:toast.published.action'), blockKey));
      setPublishConfirmOpen(false);
      onSaved(published);
    } catch (err) {
      setError(safeT(uiLocale, (err as { message?: string }).message ?? 'errors:code.INTERNAL'));
    } finally {
      setIsPublishing(false);
    }
  }

  return (
    <>
      <p className={cn(styles['versionLine'])}>
        {currentRow ? t(`adminCms:blockList.status.${currentRow.status}`, { version: currentRow.version }) : t('adminCms:blockList.status.missing')}
      </p>

      <Textarea
        label={t('adminCms:editor.field.content')}
        hint={t('adminCms:editor.field.content.hint')}
        rows={8}
        value={draftHtml}
        onChange={(event) => setDraftHtml(event.target.value)}
      />

      {error && (
        <p role="alert" className={cn(styles['error'])}>
          {error}
        </p>
      )}

      <div className={cn(styles['actions'])}>
        <Button variant="secondary" state={isSaving ? 'loading' : 'idle'} disabled={!isDirty} onClick={() => void handleSaveDraft()}>
          {t('adminCms:editor.action.saveDraft')}
        </Button>
        <Button
          variant="primary"
          disabled={!currentRow || isDirty}
          {...(!currentRow
            ? { disabledReason: t('adminCms:editor.publish.disabledReason.noDraft') }
            : isDirty
              ? { disabledReason: t('adminCms:editor.publish.disabledReason.unsavedChanges') }
              : {})}
          onClick={() => setPublishConfirmOpen(true)}
        >
          {t('adminCms:editor.action.publish')}
        </Button>
      </div>

      <section className={cn(styles['previewSection'])}>
        <h3 className={cn(styles['previewTitle'])}>{t('adminCms:editor.preview.title')}</h3>
        {currentRow ? (
          // Sanitized client-side via `sanitizeManuscriptHtml` (see the
          // module doc comment above) before this ever reaches
          // `dangerouslySetInnerHTML` — never the raw `draftHtml` textarea
          // state, only a SAVED row's returned content.
          <div className={cn(styles['previewBody'])} dangerouslySetInnerHTML={{ __html: sanitizeManuscriptHtml(extractHtml(currentRow)) }} />
        ) : (
          <p className={cn(styles['previewEmpty'])}>{t('adminCms:editor.preview.unsaved')}</p>
        )}
      </section>

      <ConfirmDialog
        open={publishConfirmOpen}
        onOpenChange={setPublishConfirmOpen}
        action={t('adminCms:confirm.publish.action')}
        assetName={`${blockKey} (${locale.toUpperCase()})`}
        loading={isPublishing}
        onConfirm={() => void handlePublish()}
      />
    </>
  );
}
